// Scheduled messages: preset times, labels, and the same send-window rules
// the backend enforces (chat_info/scheduled_messages.py), so the picker never
// offers a time the server will refuse.
// Mirrors mobile_app/components/shared/chat/scheduledMessageUtils.js (keep in sync).
import dayjs from "dayjs";

export const PRESET_HOUR = 9;
export const MAX_SCHEDULE_DAYS = 120;
// Stream signs file links for 14 days; a message with files must go out a
// day before its earliest link expires.
const ATTACHMENT_LINK_BUFFER_MS = 24 * 60 * 60 * 1000;

const atPresetHour = (day) =>
  day.hour(PRESET_HOUR).minute(0).second(0).millisecond(0);

// Slack's presets on the device's clock: two "later today" whole hours (the
// hour after next, then one more, so 10:40 offers 12:00 PM and 1:00 PM) while
// they're still today, then tomorrow and next Monday at 9:00 AM. When
// tomorrow is Monday the two are the same time, so only one is offered.
export const getSchedulePresets = (now = new Date()) => {
  const base = dayjs(now);
  const firstLater = base.add(1, "hour").startOf("hour").add(1, "hour");
  const presets = [firstLater, firstLater.add(1, "hour")]
    .filter((time) => time.isSame(base, "day"))
    .map((time, index) => ({ key: `later-${index}`, sendAt: time.toDate() }));
  const tomorrow = atPresetHour(base.add(1, "day"));
  const daysToMonday = (8 - base.day()) % 7 || 7;
  const nextMonday = atPresetHour(base.add(daysToMonday, "day"));
  presets.push({ key: "tomorrow", sendAt: tomorrow.toDate() });
  if (!nextMonday.isSame(tomorrow)) {
    presets.push({ key: "monday", sendAt: nextMonday.toDate() });
  }
  return presets;
};

// "today at 3:00 PM", "tomorrow at 9:00 AM", "Mon, Nov 2 at 9:00 AM"
export const formatScheduledTime = (value, now = new Date()) => {
  const when = dayjs(value);
  const today = dayjs(now).startOf("day");
  const time = when.format("h:mm A");
  if (when.isSame(today, "day")) return `today at ${time}`;
  if (when.isSame(today.add(1, "day"), "day")) return `tomorrow at ${time}`;
  if (when.isSame(today, "year")) return `${when.format("ddd, MMM D")} at ${time}`;
  return `${when.format("MMM D, YYYY")} at ${time}`;
};

// Preset menu label: "Later today at 12:00 PM", "Tomorrow at 9:00 AM",
// "Next Monday at 9:00 AM"
export const formatPresetLabel = (preset, now = new Date()) => {
  const when = dayjs(preset.sendAt);
  const time = when.format("h:mm A");
  if (when.isSame(dayjs(now), "day")) return `Later today at ${time}`;
  if (when.isSame(dayjs(now).add(1, "day"), "day")) return `Tomorrow at ${time}`;
  return `Next ${when.format("dddd")} at ${time}`;
};

const collectStrings = (value, out) => {
  if (typeof value === "string") out.push(value);
  else if (Array.isArray(value)) value.forEach((item) => collectStrings(item, out));
  else if (value && typeof value === "object") {
    Object.values(value).forEach((item) => collectStrings(item, out));
  }
  return out;
};

// The latest a message carrying these attachments can be sent, or null.
export const getAttachmentDeadline = (attachments) => {
  let earliest = null;
  collectStrings(attachments || [], []).forEach((value) => {
    if (!value.includes("Expires=")) return;
    let expires;
    try {
      expires = Number(new URL(value).searchParams.get("Expires"));
    } catch (error) {
      return;
    }
    if (!Number.isFinite(expires) || expires <= 0) return;
    const expiresAt = expires * 1000;
    if (earliest === null || expiresAt < earliest) earliest = expiresAt;
  });
  return earliest === null ? null : new Date(earliest - ATTACHMENT_LINK_BUFFER_MS);
};

// Daily "Working today / tomorrow" channels are deleted after their date, so a
// message there has to send by the end of that day on the STORE's clock. The
// server stamps that moment on the channel as send_by; a channel without it
// (created before the stamp, or a store without coordinates) gets no cap here
// and the server's own check still applies.
export const getChannelDeadline = (channel) => {
  const sendBy = channel?.data?.send_by;
  if (!sendBy) return null;
  const at = new Date(sendBy);
  return Number.isNaN(at.getTime()) ? null : at;
};

// 120 days of elapsed time, as the server counts it (not 120 calendar days,
// which gains or loses an hour across a DST change).
const scheduleHorizon = (now) =>
  new Date(new Date(now).getTime() + MAX_SCHEDULE_DAYS * 24 * 60 * 60 * 1000);

export const getLatestScheduleTime = (attachments, now = new Date(), channelDeadline = null) => {
  const limits = [
    scheduleHorizon(now),
    getAttachmentDeadline(attachments),
    // The deadline itself is too late, so the last pickable minute is before it.
    channelDeadline && new Date(channelDeadline.getTime() - 60 * 1000),
  ].filter(Boolean);
  return new Date(Math.min(...limits.map((limit) => limit.getTime())));
};

export const getScheduleWindowError = (sendAt, attachments, now = new Date(), channelDeadline = null) => {
  if (!sendAt || Number.isNaN(new Date(sendAt).getTime())) {
    return "Pick a valid time to send.";
  }
  const when = new Date(sendAt);
  if (when <= now) return "Pick a time in the future.";
  if (when > scheduleHorizon(now)) {
    return "Messages can be scheduled up to 120 days ahead.";
  }
  const deadline = getAttachmentDeadline(attachments);
  if (deadline && when > deadline) {
    return "Messages with files can be scheduled up to 13 days after the files were added.";
  }
  if (channelDeadline && when >= channelDeadline) {
    return "Messages in a daily channel have to send by the end of that day.";
  }
  return null;
};

// The fields we store, from the message Stream's composer built.
export const toSchedulePayload = (message) => ({
  text: message?.text || "",
  attachments: message?.attachments || [],
  mentioned_users: (message?.mentioned_users || []).map((user) =>
    typeof user === "object" ? user?.id : user
  ),
  parent_id: message?.parent_id || null,
  show_in_channel: Boolean(message?.parent_id && message?.show_in_channel),
  quoted_message_id: message?.quoted_message_id || null,
});

// Messages still waiting or failed, per channel: { [cid]: { pending, failed } }
export const countScheduledByChannel = (scheduledMessages) =>
  (scheduledMessages || []).reduce((counts, row) => {
    const entry = counts[row.cid] || { pending: 0, failed: 0 };
    if (row.status === "failed") entry.failed += 1;
    else entry.pending += 1;
    counts[row.cid] = entry;
    return counts;
  }, {});

// One id per draft, reused on retry, so a schedule request whose response
// was lost can't create a second copy (the server keys the row on it).
export const newClientMessageId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    return (char === "x" ? random : (random & 0x3) | 0x8).toString(16);
  });
};

// The draft's id: kept while the same draft (same text) is retried, new once
// it changes. `holder` is a ref the caller keeps.
export const draftClientMessageId = (holder, draftSignature) => {
  if (!holder.current || holder.current.signature !== draftSignature) {
    holder.current = { signature: draftSignature, id: newClientMessageId() };
  }
  return holder.current.id;
};

// Snapshot of everything Stream's composer holds, returning a function that
// puts it back: the same pattern Stream's own submit handler uses, so a
// message can be cleared while its schedule request is in flight (nothing
// left for Send or Enter to post a second copy of) and restored on failure.
export const takeComposerSnapshot = (messageComposer) => {
  const stores = [
    messageComposer.state,
    messageComposer.textComposer?.state,
    messageComposer.attachmentManager?.state,
    messageComposer.linkPreviewsManager?.state,
    messageComposer.pollComposer?.state,
    messageComposer.customDataManager?.state,
  ].filter(Boolean);
  const values = stores.map((store) => store.getLatestValue());
  return () => stores.forEach((store, index) => store.next(values[index]));
};

// A schedule failed after the user had started a new message: bring the
// failed one back (text and files) and keep what they'd started after it,
// so neither is lost.
export const restoreComposerKeepingNewDraft = (messageComposer, restoreComposer) => {
  const newText = messageComposer.textComposer?.text || "";
  const newAttachments = messageComposer.attachmentManager?.attachments || [];
  restoreComposer();
  if (newText.trim()) {
    const restoredText = messageComposer.textComposer.text || "";
    messageComposer.textComposer.setText(restoredText ? `${restoredText}\n\n${newText}` : newText);
  }
  if (newAttachments.length) {
    messageComposer.attachmentManager.upsertAttachments(newAttachments);
  }
};

export const isScheduledStreamMessageId = (messageId) =>
  typeof messageId === "string" && messageId.startsWith("sched-");
